import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { motion } from 'framer-motion';
import { MagnifyingGlassIcon, AcademicCapIcon } from '@heroicons/react/24/outline';

// School data interface
interface School {
  id: string;
  name: string;
  sector: string;
  type: string;
  city: string;
  lat: number;
  lng: number;
  independent: boolean;
}

// Transform raw data to School interface
function toSchool(r: any): School | null {
  const id = r.ACARAId || r.LocalId || r.RefId;
  const name = r.SchoolName as string;
  const sector = r.SchoolSector as string;
  const city = r.AddressList?.[0]?.City as string | undefined;
  const lat = r.AddressList?.[0]?.GridLocation?.Latitude;
  const lng = r.AddressList?.[0]?.GridLocation?.Longitude;
  const type = r.SchoolType as string;
  const independent = r.IndependentSchool === 'Y';
  if (!id || !name || lat == null || lng == null) return null;
  return { id: String(id), name, sector, city: city || '', type, lat: Number(lat), lng: Number(lng), independent };
}

const GTMv2Slide07SchoolMap: React.FC = () => {
  const [schools, setSchools] = useState<School[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [sectorFilter, setSectorFilter] = useState<'all' | 'Gov' | 'NG'>('all');
  const [typeFilter, setTypeFilter] = useState<'all' | 'Prim' | 'Sec' | 'Pri/Sec'>('all');
  const [selectedSchool, setSelectedSchool] = useState<School | null>(null);
  const [mapLoaded, setMapLoaded] = useState(false);
  const mapRef = useRef<any>(null);
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const markersRef = useRef<any[]>([]);
  const clustererRef = useRef<any>(null);

  // Load schools data
  useEffect(() => {
    fetch('/schools-nsw.json')
      .then(res => res.json())
      .then((data: any[]) => {
        const mapped = data.map(toSchool).filter((s): s is School => s !== null);
        setSchools(mapped);
      })
      .catch(err => console.error('Failed to load schools:', err));
  }, []);

  // Load Google Maps
  useEffect(() => {
    const win = window as any;
    if (win.google?.maps) {
      setMapLoaded(true);
      return;
    }

    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=AIzaSyDfS-i36qysUaEaA1Jk2x6v9KPq7cquCug&v=weekly&libraries=marker`;
    script.async = true;
    script.defer = true;
    script.onload = () => setMapLoaded(true);
    document.head.appendChild(script);

    // Load marker clusterer
    const clustererScript = document.createElement('script');
    clustererScript.src = 'https://unpkg.com/@googlemaps/markerclusterer/dist/index.min.js';
    clustererScript.async = true;
    document.head.appendChild(clustererScript);

    return () => {
      // Cleanup if needed
    };
  }, []);

  // Initialize map
  useEffect(() => {
    const win = window as any;
    if (!mapLoaded || !mapContainerRef.current || mapRef.current || !win.google?.maps) return;

    const map = new win.google.maps.Map(mapContainerRef.current, {
      center: { lat: -33.8688, lng: 151.2093 }, // Sydney
      zoom: 10,
      styles: [
        { elementType: 'geometry', stylers: [{ color: '#0f172a' }] },
        { elementType: 'labels.text.stroke', stylers: [{ color: '#0f172a' }] },
        { elementType: 'labels.text.fill', stylers: [{ color: '#64748b' }] },
        { featureType: 'administrative', elementType: 'geometry.stroke', stylers: [{ color: '#334155' }] },
        { featureType: 'road', elementType: 'geometry', stylers: [{ color: '#1e293b' }] },
        { featureType: 'road', elementType: 'geometry.stroke', stylers: [{ color: '#334155' }] },
        { featureType: 'water', elementType: 'geometry', stylers: [{ color: '#0c4a6e' }] },
        { featureType: 'poi', stylers: [{ visibility: 'off' }] },
        { featureType: 'transit', stylers: [{ visibility: 'off' }] },
      ],
      disableDefaultUI: true,
      zoomControl: true,
      mapTypeControl: false,
      streetViewControl: false,
      fullscreenControl: false,
    });

    mapRef.current = map;
  }, [mapLoaded]);

  // Filter schools
  const filteredSchools = useMemo(() => {
    return schools.filter(s => {
      if (sectorFilter !== 'all' && s.sector !== sectorFilter) return false;
      if (typeFilter !== 'all' && s.type !== typeFilter) return false;
      if (searchTerm.length >= 2) {
        const search = searchTerm.toLowerCase();
        if (!s.name.toLowerCase().includes(search) && !s.city.toLowerCase().includes(search)) {
          return false;
        }
      }
      return true;
    });
  }, [schools, searchTerm, sectorFilter, typeFilter]);

  // Update markers when filtered schools change
  useEffect(() => {
    const win = window as any;
    if (!mapRef.current || !mapLoaded || !win.google?.maps) return;

    // Clear existing markers
    markersRef.current.forEach(m => m.setMap(null));
    markersRef.current = [];
    if (clustererRef.current) {
      clustererRef.current.clearMarkers();
    }

    // Create new markers (limit to viewport or first 1000 for performance)
    const bounds = mapRef.current.getBounds();
    const visibleSchools = filteredSchools.filter(s => {
      if (!bounds) return true;
      return bounds.contains({ lat: s.lat, lng: s.lng });
    }).slice(0, 1000);

    const markers = visibleSchools.map(school => {
      const marker = new win.google.maps.Marker({
        position: { lat: school.lat, lng: school.lng },
        title: school.name,
        icon: {
          path: win.google.maps.SymbolPath.CIRCLE,
          fillColor: school.sector === 'Gov' ? '#0ea5e9' : '#10b981',
          fillOpacity: 0.9,
          strokeColor: '#ffffff',
          strokeWeight: 1,
          scale: selectedSchool?.id === school.id ? 10 : 6,
        },
      });

      marker.addListener('click', () => {
        setSelectedSchool(school);
        mapRef.current?.panTo({ lat: school.lat, lng: school.lng });
      });

      return marker;
    });

    markersRef.current = markers;

    // Use MarkerClusterer if available
    if (win.markerClusterer?.MarkerClusterer) {
      clustererRef.current = new win.markerClusterer.MarkerClusterer({
        map: mapRef.current,
        markers,
      });
    } else {
      markers.forEach(m => m.setMap(mapRef.current));
    }
  }, [filteredSchools, mapLoaded, selectedSchool]);

  // Stats
  const stats = useMemo(() => {
    const gov = filteredSchools.filter(s => s.sector === 'Gov').length;
    const nonGov = filteredSchools.filter(s => s.sector === 'NG').length;
    const independent = filteredSchools.filter(s => s.independent).length;
    const primary = filteredSchools.filter(s => s.type === 'Prim').length;
    const secondary = filteredSchools.filter(s => s.type === 'Sec').length;
    return { total: filteredSchools.length, gov, nonGov, independent, primary, secondary };
  }, [filteredSchools]);

  const handleSchoolClick = useCallback((school: School) => {
    setSelectedSchool(school);
    mapRef.current?.panTo({ lat: school.lat, lng: school.lng });
    mapRef.current?.setZoom(14);
  }, []);

  return (
    <div className="h-full flex gap-4 p-4">
      {/* Sidebar */}
      <motion.div
        initial={{ opacity: 0, x: -20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.5 }}
        className="w-80 flex flex-col rounded-xl overflow-hidden"
        style={{ background: 'rgba(15, 23, 42, 0.95)', border: '1px solid rgba(255,255,255,0.1)' }}
      >
        {/* Header */}
        <div className="p-4 border-b border-white/10">
          <h3 className="text-lg font-bold text-white mb-1">NSW Schools</h3>
          <p className="text-sm text-[#64748b]">
            <span className="text-[#0ea5e9] font-semibold">{stats.total.toLocaleString()}</span> schools shown
          </p>
        </div>

        {/* Search */}
        <div className="p-3 border-b border-white/10">
          <div className="relative">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#64748b]" />
            <input
              type="text"
              placeholder="Search schools or suburbs..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-9 pr-3 py-2 bg-white/5 border border-white/10 rounded-lg text-sm text-white placeholder-[#64748b] focus:outline-none focus:border-[#0ea5e9]/50"
            />
          </div>
        </div>

        {/* Filters */}
        <div className="p-3 border-b border-white/10 space-y-2">
          <div className="flex gap-2">
            <button
              onClick={() => setSectorFilter('all')}
              className={`flex-1 px-2 py-1.5 rounded text-xs font-medium transition-all ${sectorFilter === 'all' ? 'bg-[#0ea5e9] text-white' : 'bg-white/5 text-[#94a3b8] hover:bg-white/10'}`}
            >
              All
            </button>
            <button
              onClick={() => setSectorFilter('Gov')}
              className={`flex-1 px-2 py-1.5 rounded text-xs font-medium transition-all ${sectorFilter === 'Gov' ? 'bg-[#0ea5e9] text-white' : 'bg-white/5 text-[#94a3b8] hover:bg-white/10'}`}
            >
              Government
            </button>
            <button
              onClick={() => setSectorFilter('NG')}
              className={`flex-1 px-2 py-1.5 rounded text-xs font-medium transition-all ${sectorFilter === 'NG' ? 'bg-[#10b981] text-white' : 'bg-white/5 text-[#94a3b8] hover:bg-white/10'}`}
            >
              Non-Gov
            </button>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setTypeFilter('all')}
              className={`flex-1 px-2 py-1.5 rounded text-xs font-medium transition-all ${typeFilter === 'all' ? 'bg-[#8b5cf6] text-white' : 'bg-white/5 text-[#94a3b8] hover:bg-white/10'}`}
            >
              All Types
            </button>
            <button
              onClick={() => setTypeFilter('Prim')}
              className={`flex-1 px-2 py-1.5 rounded text-xs font-medium transition-all ${typeFilter === 'Prim' ? 'bg-[#f59e0b] text-white' : 'bg-white/5 text-[#94a3b8] hover:bg-white/10'}`}
            >
              Primary
            </button>
            <button
              onClick={() => setTypeFilter('Sec')}
              className={`flex-1 px-2 py-1.5 rounded text-xs font-medium transition-all ${typeFilter === 'Sec' ? 'bg-[#ec4899] text-white' : 'bg-white/5 text-[#94a3b8] hover:bg-white/10'}`}
            >
              Secondary
            </button>
          </div>
        </div>

        {/* Stats Summary */}
        <div className="p-3 border-b border-white/10 grid grid-cols-2 gap-2">
          <div className="bg-[#0ea5e9]/10 rounded-lg p-2 text-center">
            <div className="text-lg font-bold text-[#0ea5e9]">{stats.gov.toLocaleString()}</div>
            <div className="text-[10px] text-[#64748b]">Government</div>
          </div>
          <div className="bg-[#10b981]/10 rounded-lg p-2 text-center">
            <div className="text-lg font-bold text-[#10b981]">{stats.nonGov.toLocaleString()}</div>
            <div className="text-[10px] text-[#64748b]">Non-Government</div>
          </div>
          <div className="bg-[#f59e0b]/10 rounded-lg p-2 text-center">
            <div className="text-lg font-bold text-[#f59e0b]">{stats.independent.toLocaleString()}</div>
            <div className="text-[10px] text-[#64748b]">Independent</div>
          </div>
          <div className="bg-[#8b5cf6]/10 rounded-lg p-2 text-center">
            <div className="text-lg font-bold text-[#8b5cf6]">{stats.primary.toLocaleString()}</div>
            <div className="text-[10px] text-[#64748b]">Primary</div>
          </div>
        </div>

        {/* School List */}
        <div className="flex-1 overflow-y-auto">
          {filteredSchools.slice(0, 100).map((school) => (
            <div
              key={school.id}
              onClick={() => handleSchoolClick(school)}
              className={`px-3 py-2 cursor-pointer transition-all border-b border-white/5 hover:bg-white/5 ${selectedSchool?.id === school.id ? 'bg-[#0ea5e9]/20 border-l-2 border-l-[#0ea5e9]' : ''}`}
            >
              <div className="flex items-start gap-2">
                <div className={`w-2 h-2 rounded-full mt-1.5 flex-shrink-0 ${school.sector === 'Gov' ? 'bg-[#0ea5e9]' : 'bg-[#10b981]'}`} />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-white truncate">{school.name}</div>
                  <div className="text-xs text-[#64748b] flex items-center gap-2">
                    <span>{school.city}</span>
                    <span className="text-[#475569]">•</span>
                    <span>{school.type === 'Prim' ? 'Primary' : school.type === 'Sec' ? 'Secondary' : school.type}</span>
                  </div>
                </div>
              </div>
            </div>
          ))}
          {filteredSchools.length > 100 && (
            <div className="px-3 py-4 text-center text-xs text-[#64748b]">
              + {(filteredSchools.length - 100).toLocaleString()} more schools
            </div>
          )}
        </div>
      </motion.div>

      {/* Map Container */}
      <motion.div
        initial={{ opacity: 0, scale: 0.98 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.5, delay: 0.2 }}
        className="flex-1 rounded-xl overflow-hidden relative"
        style={{ border: '1px solid rgba(255,255,255,0.1)' }}
      >
        {/* Map */}
        <div ref={mapContainerRef} className="w-full h-full" />

        {/* Legend */}
        <div
          className="absolute bottom-4 left-4 p-3 rounded-lg"
          style={{ background: 'rgba(15, 23, 42, 0.9)', border: '1px solid rgba(255,255,255,0.1)' }}
        >
          <div className="text-xs font-medium text-white mb-2">Legend</div>
          <div className="space-y-1.5">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-[#0ea5e9]" />
              <span className="text-xs text-[#94a3b8]">Government School</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-[#10b981]" />
              <span className="text-xs text-[#94a3b8]">Non-Government School</span>
            </div>
          </div>
        </div>

        {/* Selected School Info */}
        {selectedSchool && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="absolute bottom-4 right-4 p-4 rounded-lg max-w-xs"
            style={{ background: 'rgba(15, 23, 42, 0.95)', border: '1px solid rgba(255,255,255,0.1)' }}
          >
            <div className="flex items-start gap-3">
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${selectedSchool.sector === 'Gov' ? 'bg-[#0ea5e9]/20' : 'bg-[#10b981]/20'}`}>
                <AcademicCapIcon className={`w-5 h-5 ${selectedSchool.sector === 'Gov' ? 'text-[#0ea5e9]' : 'text-[#10b981]'}`} />
              </div>
              <div className="flex-1">
                <h4 className="text-sm font-bold text-white">{selectedSchool.name}</h4>
                <p className="text-xs text-[#64748b] mt-0.5">{selectedSchool.city}</p>
                <div className="flex items-center gap-2 mt-2">
                  <span className={`px-2 py-0.5 rounded text-[10px] font-medium ${selectedSchool.sector === 'Gov' ? 'bg-[#0ea5e9]/20 text-[#0ea5e9]' : 'bg-[#10b981]/20 text-[#10b981]'}`}>
                    {selectedSchool.sector === 'Gov' ? 'Government' : 'Non-Government'}
                  </span>
                  <span className="px-2 py-0.5 rounded text-[10px] font-medium bg-[#8b5cf6]/20 text-[#8b5cf6]">
                    {selectedSchool.type === 'Prim' ? 'Primary' : selectedSchool.type === 'Sec' ? 'Secondary' : selectedSchool.type}
                  </span>
                </div>
              </div>
            </div>
          </motion.div>
        )}

        {/* Title Overlay */}
        <div
          className="absolute top-4 left-1/2 -translate-x-1/2 px-6 py-3 rounded-xl"
          style={{ background: 'rgba(15, 23, 42, 0.9)', border: '1px solid rgba(255,255,255,0.1)' }}
        >
          <h2 className="text-xl font-bold text-white text-center">
            <span className="text-[#0ea5e9]">{schools.length.toLocaleString()}</span> Schools in NSW
          </h2>
          <p className="text-xs text-[#64748b] text-center mt-1">Our Initial Target Market</p>
        </div>
      </motion.div>
    </div>
  );
};

export default GTMv2Slide07SchoolMap;
